import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/api";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import {
  FaCheckCircle,
  FaDownload,
  FaEdit,
  FaExclamationCircle,
  FaFileInvoiceDollar,
  FaGasPump,
  FaHandshake,
  FaInfoCircle,
  FaSave,
  FaTimes,
  FaTrash,
  FaVial,
} from "react-icons/fa";
import "./CSS/NozzleReadings.css";
import "./CSS/ReadingsPremium.css";


const readStoredRole = () => {
  const rawRole = localStorage.getItem("role");
  if (!rawRole) return "";
  try {
    return String(JSON.parse(rawRole)).trim().toLowerCase();
  } catch {
    return String(rawRole).trim().toLowerCase();
  }
};

function NotificationToast({ notice, onClose }) {
  if (!notice) return null;

  const Icon =
    notice.type === "success"
      ? FaCheckCircle
      : notice.type === "error" || notice.type === "warning"
      ? FaExclamationCircle
      : FaInfoCircle;

  return (
    <div
      className={`nread-toast ${notice.type}`}
      role={notice.type === "error" || notice.type === "warning" ? "alert" : "status"}
      aria-live={notice.type === "error" || notice.type === "warning" ? "assertive" : "polite"}
      key={notice.id}
    >
      <div className="nread-toast-icon">
        <Icon aria-hidden="true" />
      </div>
      <div className="nread-toast-copy">
        <strong>{notice.title}</strong>
        <span>{notice.message}</span>
      </div>
      <button className="nread-toast-close" type="button" onClick={onClose} aria-label="Close message">
        <FaTimes aria-hidden="true" />
      </button>
    </div>
  );
}

export default function NozzleReadings() {
  const [readings, setReadings] = useState([]);
  const [nozzles, setNozzles] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [lubeProducts, setLubeProducts] = useState([]);
  const [showConfirmSave, setShowConfirmSave] = useState(false);
  const [form, setForm] = useState({
    pump_name: "",
    nozzle_id: "",
    opening_reading: "",
    closing_reading: "",
    opening_reading_datetime: "",
    closing_reading_datetime: "",
  });
  
  const [pumpNozzleReadings, setPumpNozzleReadings] = useState({});
  const [editId, setEditId] = useState(null);

  const [testingMode, setTestingMode] = useState("no"); // yes | no

  const [showCustomerCredit, setShowCustomerCredit] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);
  const [savingRecon, setSavingRecon] = useState(false);
  const [notice, setNotice] = useState(null);
  const resumeReconWindowRef = useRef(false);

  const [customerCredits, setCustomerCredits] = useState([
    { customer_id: "", fuel_type: "", amount: "", volume: "", comment: "" },
  ]);
  const [customerCreditLimitMap, setCustomerCreditLimitMap] = useState({});

  const [recon, setRecon] = useState({
    date: "",
    employee_id: "",
    shift: "",
    cash: 0,
    upi: 0,
    card: 0,
    hp_pay: 0,
    credit: 0,
    test: 0,
    others: 0,
    fleet: 0,
    misc: 0,
    discount_amount: 0,
    comment: "",
  });

  const [lubeReport, setLubeReport] = useState({
    total_amount: 0,
    total_quantity: 0,
    sales: [],
    summaryByProduct: [],
    startDateTime: "",
    endDateTime: "",
  });

  const [missingLubeRows, setMissingLubeRows] = useState([
    {
      product_id: "",
      quantity: "",
      unit_price: 0,
      total_amount: 0,
      product_name: "",
      comment: "",
    },
  ]);

  const token = localStorage.getItem("token");
  const isManager = readStoredRole() === "manager";
  const TEST_LITERS_PER_LINE = 5;
  const formatMoney = (value) =>
    new Intl.NumberFormat("en-IN", {
      currency: "INR",
      maximumFractionDigits: 0,
      style: "currency",
    }).format(parseFloat(value || 0));

  useEffect(() => {
    fetchReadings();
    fetchNozzles();
    fetchEmployees();
    fetchCustomers();
    fetchLubeProducts();
  }, []);

  useEffect(() => {
    if (!notice) return undefined;
    const timer = setTimeout(
      () => setNotice(null),
      notice.type === "error" || notice.type === "warning" ? 5600 : 4300
    );
    return () => clearTimeout(timer);
  }, [notice]);

  const showNotice = (type, title, message) => {
    setNotice({
      id: Date.now(),
      message,
      title,
      type,
    });
  };

  const fetchCustomerCreditBlock = async (customerId) => {
    const res = await api.get("/api/users/customercredit/limit", {
      headers: { Authorization: `Bearer ${token}` },
      params: { customer_id: customerId },
    });
    return res.data || {};
  };

  const loadCustomerCreditLimit = async (customerId) => {
    if (!customerId) return null;
    const block = await fetchCustomerCreditBlock(customerId);
    setCustomerCreditLimitMap((prev) => ({
      ...prev,
      [String(customerId)]: block || {},
    }));
    return block || {};
  };

  const getCustomerDisplayName = (customerId) => {
    const found = customers.find((customer) => String(customer.id) === String(customerId));
    return found?.name || found?.full_name || found?.username || `Customer ${customerId}`;
  };

  const showPumpFirstWarning = () => {
    showNotice(
      "warning",
      "Select pump first",
      "Please select the pump first before setting the opening and closing reading."
    );
  };

  const showOpeningLockedWarning = () => {
    showNotice(
      "warning",
      "Opening reading locked",
      "You are not authorized to change the opening reading. Please contact administrator."
    );
  };

  const hasReadingValue = (value) => String(value ?? "").trim() !== "";

  const getReadingValidationRows = () => {
    const entries = Object.entries(pumpNozzleReadings);
    if (!editId) return entries;
    return entries.filter(([nozzleId]) => String(nozzleId) === String(form.nozzle_id));
  };

  const validateReadingForm = () => {
    if (!form.pump_name) {
      showPumpFirstWarning();
      return false;
    }

    if (!form.opening_reading_datetime || !form.closing_reading_datetime) {
      showNotice(
        "warning",
        "Time required",
        "Can't save the readings without opening and closing time. Please check again for the opening and closing time."
      );
      return false;
    }

    const openingMs = Date.parse(form.opening_reading_datetime);
    const closingMs = Date.parse(form.closing_reading_datetime);
    if (!Number.isNaN(openingMs) && !Number.isNaN(closingMs) && closingMs <= openingMs) {
      showNotice(
        "warning",
        "Check closing time",
        "Closing time should be after the opening time before saving readings."
      );
      return false;
    }

    const validationRows = getReadingValidationRows();
    const rowsWithClosing = validationRows.filter(([, vals]) =>
      hasReadingValue(vals?.closing_reading)
    );

    if (rowsWithClosing.length === 0) {
      showNotice(
        "warning",
        "Closing reading required",
        "Please enter a closing reading for at least one nozzle of the selected pump."
      );
      return false;
    }

    const missingOpening = rowsWithClosing.find(([, vals]) =>
      !hasReadingValue(vals?.opening_reading)
    );
    if (missingOpening) {
      showNotice(
        "warning",
        "Opening reading required",
        isManager
          ? "Opening reading is locked for managers. Please contact admin to set the opening reading."
          : "Please enter the opening reading for every nozzle that has a closing reading."
      );
      return false;
    }

    const invalidReading = rowsWithClosing.find(([, vals]) => {
      const opening = parseFloat(vals.opening_reading);
      const closing = parseFloat(vals.closing_reading);
      return Number.isNaN(opening) || Number.isNaN(closing) || closing <= opening;
    });

    if (invalidReading) {
      showNotice(
        "warning",
        "Check readings",
        "Closing reading should be greater than the opening reading."
      );
      return false;
    }

    return true;
  };

  const resetCustomerCredits = () => {
    setCustomerCredits([
      { customer_id: "", fuel_type: "", amount: "", volume: "", comment: "" },
    ]);
    setCustomerCreditLimitMap({});
  };

  const resetMissingLubeRows = () => {
    setMissingLubeRows([
      {
        product_id: "",
        quantity: "",
        unit_price: 0,
        total_amount: 0,
        product_name: "",
        comment: "",
      },
    ]);
  };

  const fetchCustomers = async () => {
    const res = await api.get("/api/users/customers", {
      headers: { Authorization: `Bearer ${token}` },
    });
    setCustomers(res.data || []);
  };

  const fetchReadings = async () => {
    const res = await api.get("/api/nozzle-readings", {
      headers: { Authorization: `Bearer ${token}` },
    });
    setReadings(res.data || []);
  };

  const fetchNozzles = async () => {
  const res = await api.get("/api/nozzles", {
    headers: { Authorization: `Bearer ${token}` },
  });
  const rows = res.data || [];
  setNozzles(rows);
  return rows;
};

  const fetchEmployees = async () => {
    const res = await api.get("/api/users", {
      params: { scope: "tenant" },
      headers: { Authorization: `Bearer ${token}` },
    });
    setEmployees((res.data || []).filter((emp) => emp.role === "operator" || emp.role === "manager"));
  };

  const fetchLubeProducts = async () => {
    try {
      const res = await api.get("/api/lube-stock", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLubeProducts(res.data || []);
    } catch (err) {
      console.error("Error fetching lube products:", err);
      setLubeProducts([]);
    }
  };

 const formatDatetime = (input) => {
  if (!input) return "";
  let s = String(input).trim();

  s = s.replace(/\.\d+$/, "");
  s = s.replace(" ", "T");
  s = s.replace("Z", "");

  return s.slice(0, 16);
};

  // ONLY DISPLAY FIX: no timezone conversion, no Date parsing
  const displayDateTime = (value) => {
    if (!value) return "";
    let s = String(value).trim();

    // remove milliseconds if present
    s = s.replace(/\.\d+Z$/, "");
    s = s.replace(/\.\d+$/, "");

    // if backend sends ISO-like string, just make it readable without converting
    s = s.replace("T", " ");
    s = s.replace("Z", "");

    return s.slice(0, 19);
  };
  
  const toSqlDateTime = (datetimeLocal) => {
    if (!datetimeLocal) return "";
    const s = String(datetimeLocal).replace("T", " ");
    return s.length === 16 ? `${s}:00` : s;
  };

  const n = (v) => parseFloat(v) || 0;

  const getPumpName = (nozzle_id) =>
    nozzles.find((nz) => String(nz.id) === String(nozzle_id))?.pump_name || "";

  const getFuelName = (nozzle_id) =>
    nozzles.find((nz) => String(nz.id) === String(nozzle_id))?.fuel_type || "";

  const getNozzleById = (nozzleId) =>
    nozzles.find((nz) => String(nz.id) === String(nozzleId));

  const getFuelColor = (fuelType) => {
    if (!fuelType) return "#6b7280";
    const ft = String(fuelType).toLowerCase();
    if (ft.includes("power")) return "#ef4444";
    if (ft.includes("diesel")) return "#3b82f6";
    return "#22c55e";
  };

  const computeRemaining = (total, reconState) => {
    return (
      total -
      n(reconState.discount_amount) -
      (n(reconState.cash) +
        n(reconState.upi) +
        n(reconState.card) +
        n(reconState.hp_pay) +
        n(reconState.credit) +
        n(reconState.others) +
        n(reconState.fleet) +
        n(reconState.misc))
    );
  };

  const getRateForReading = (reading) => {
    const nozzle = getNozzleById(reading.nozzle_id);

    if (nozzle?.rate != null && nozzle.rate !== "") {
      return parseFloat(nozzle.rate) || 0;
    }

    const dispensed = parseFloat(reading.dispensed_volume || 0);
    const amount = parseFloat(reading.amount || 0);

    if (dispensed > 0 && amount > 0) {
      return amount / dispensed;
    }

    return 0;
  };

  const getAdjustedReading = (reading) => {
    const originalDispensed = parseFloat(reading.dispensed_volume || 0);
    const originalAmount = parseFloat(reading.amount || 0);
    const rate = getRateForReading(reading);

    if (testingMode !== "yes") {
      return {
        ...reading,
        testing_liters: 0,
        testing_amount: 0,
        actual_dispensed_volume: originalDispensed,
        actual_amount: originalAmount,
      };
    }

    const testingLiters = Math.min(TEST_LITERS_PER_LINE, originalDispensed);
    const testingAmount = testingLiters * rate;

    const actualVolume = Math.max(originalDispensed - testingLiters, 0);
    const actualAmount = Math.max(originalAmount - testingAmount, 0);

    return {
      ...reading,
      testing_liters: testingLiters,
      testing_amount: testingAmount,
      actual_dispensed_volume: actualVolume,
      actual_amount: actualAmount,
    };
  };

  const save = async () => {
    try {
      if (!validateReadingForm()) {
        return;
      }

      if (editId) {
        const nozzleId = form.nozzle_id;
        const rz = pumpNozzleReadings[nozzleId];

        if (!nozzleId || !rz) {
          showNotice("warning", "No nozzle selected", "No nozzle data was found for update.");
          return;
        }

        const payload = {
          nozzle_id: nozzleId,
          opening_reading: parseFloat(rz.opening_reading),
          closing_reading: parseFloat(rz.closing_reading),
          opening_reading_datetime: form.opening_reading_datetime,
          closing_reading_datetime: form.closing_reading_datetime,
        };

        await api.put(`/api/nozzle-readings/${editId}`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        });
      } else {
        const entries = Object.entries(pumpNozzleReadings);

        const payloads = entries
          .filter(
            ([, vals]) =>
              hasReadingValue(vals.opening_reading) && hasReadingValue(vals.closing_reading)
          )
          .map(([nozzleId, vals]) => ({
            nozzle_id: nozzleId,
            opening_reading: parseFloat(vals.opening_reading),
            closing_reading: parseFloat(vals.closing_reading),
            opening_reading_datetime: form.opening_reading_datetime,
            closing_reading_datetime: form.closing_reading_datetime,
          }));

        if (payloads.length === 0) {
          showNotice(
            "warning",
            "Closing reading required",
            "Please enter a closing reading for at least one nozzle of the selected pump."
          );
          return;
        }

        for (const payload of payloads) {
          await api.post("/api/nozzle-readings", payload, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
      }

      reset();
      fetchReadings();
      fetchNozzles();
      showNotice(
        "success",
        editId ? "Reading updated" : "Readings saved",
        editId ? "Nozzle reading was updated successfully." : "Nozzle readings were saved successfully."
      );
    } catch (err) {
      console.error("Error saving nozzle readings:", err);
      showNotice("error", "Save failed", "Error saving readings. Please try again.");
    }
  };

  const reset = () => {
    setForm((prev) => ({
      ...prev,
      pump_name: "",
      nozzle_id: "",
      opening_reading: "",
      closing_reading: "",
    }));
    setPumpNozzleReadings({});
    setEditId(null);
  };

  const startEdit = (r) => {
    const nozzle = nozzles.find((nz) => String(nz.id) === String(r.nozzle_id));
    const pumpName = nozzle?.pump_name || "";

    setForm((prev) => ({
      ...prev,
      pump_name: pumpName,
      nozzle_id: r.nozzle_id,
      opening_reading_datetime: formatDatetime(r.opening_reading_datetime),
      closing_reading_datetime: formatDatetime(r.closing_reading_datetime),
    }));

    if (pumpName) {
      const related = nozzles.filter((nz) => nz.pump_name === pumpName);
      const init = {};
      related.forEach((nz) => {
        init[nz.id] = {
          opening_reading:
            String(nz.id) === String(r.nozzle_id) ? r.opening_reading : "",
          closing_reading:
            String(nz.id) === String(r.nozzle_id) ? r.closing_reading : "",
        };
      });
      setPumpNozzleReadings(init);
    } else {
      setPumpNozzleReadings({
        [r.nozzle_id]: {
          opening_reading: r.opening_reading,
          closing_reading: r.closing_reading,
        },
      });
    }

    setEditId(r.id);
  };

  const del = async (id) => {
    await api.delete(`/api/nozzle-readings/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    fetchReadings();
    fetchNozzles();
  };

  const getFilteredReadings = () => readings;

  const displayedReadings = useMemo(() => {
    return getFilteredReadings().map((r) => getAdjustedReading(r));
  }, [readings, nozzles, testingMode]);

  useEffect(() => {
    if (resumeReconWindowRef.current) return;
    if (editId) return;
    if (form.pump_name || form.opening_reading_datetime || form.closing_reading_datetime) return;
    if (!displayedReadings.length || !nozzles.length) return;

    const firstPendingRow =
      displayedReadings.find((r) => r.opening_reading_datetime && r.closing_reading_datetime) ||
      displayedReadings[0];

    if (!firstPendingRow?.opening_reading_datetime || !firstPendingRow?.closing_reading_datetime) {
      return;
    }

    const pumpName = getPumpName(firstPendingRow.nozzle_id);
    if (!pumpName) return;

    const relatedNozzles = nozzles.filter((nz) => nz.pump_name === pumpName);
    const init = {};
    relatedNozzles.forEach((nz) => {
      init[nz.id] = {
        opening_reading: nz.latest_reading ?? "",
        closing_reading: "",
      };
    });

    setForm((prev) => ({
      ...prev,
      pump_name: pumpName,
      nozzle_id: "",
      opening_reading_datetime: formatDatetime(firstPendingRow.opening_reading_datetime),
      closing_reading_datetime: formatDatetime(firstPendingRow.closing_reading_datetime),
    }));
    setPumpNozzleReadings(init);
    resumeReconWindowRef.current = true;
    showNotice(
      "info",
      "Unreconciled row restored",
      "We picked the opening and closing time from the first unreconciled row so you can continue the pending reconciliation."
    );
  }, [displayedReadings, nozzles, editId, form.pump_name, form.opening_reading_datetime, form.closing_reading_datetime]);

  const fuelTotal = displayedReadings.reduce(
    (sum, r) => sum + (parseFloat(r.actual_amount) || 0),
    0
  );

  const testingTotal = displayedReadings.reduce(
    (sum, r) => sum + (parseFloat(r.testing_amount) || 0),
    0
  );

  const testingLitersTotal = displayedReadings.reduce(
    (sum, r) => sum + (parseFloat(r.testing_liters) || 0),
    0
  );

  useEffect(() => {
    setRecon((prev) => ({
      ...prev,
      test: parseFloat(testingTotal.toFixed(2)),
    }));
  }, [testingTotal]);

  const exportToExcel = () => {
    const data = displayedReadings.map((r) => ({
      Pump: getPumpName(r.nozzle_id),
      Fuel_Type: getFuelName(r.nozzle_id),
      Nozzle:
        nozzles.find((nz) => String(nz.id) === String(r.nozzle_id))?.name ||
        r.nozzle_id,
      Opening: r.opening_reading,
      Closing: r.closing_reading,
      Dispensed: parseFloat(r.actual_dispensed_volume || 0).toFixed(3),
      Testing_Liters: parseFloat(r.testing_liters || 0).toFixed(3),
      Testing_Amount: parseFloat(r.testing_amount || 0).toFixed(2),
      Amount: parseFloat(r.actual_amount || 0).toFixed(2),
      Open_Time: displayDateTime(r.opening_reading_datetime),
      Close_Time: displayDateTime(r.closing_reading_datetime),
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "NozzleReadings");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const dataBlob = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    saveAs(dataBlob, "nozzle_readings.xlsx");
  };

  const pumpMap = {};
  displayedReadings.forEach((r) => {
    const pump = getPumpName(r.nozzle_id);
    const nozzleName =
      nozzles.find((nz) => String(nz.id) === String(r.nozzle_id))?.name || "";
    if (!pumpMap[pump]) pumpMap[pump] = [];
    if (!pumpMap[pump].includes(nozzleName)) pumpMap[pump].push(nozzleName);
  });

  const { availableFuelTypes, fuelRateMap } = useMemo(() => {
    const pumpsInRecon = Object.keys(pumpMap || {}).filter(Boolean);

    const baseNozzles =
      pumpsInRecon.length > 0
        ? nozzles.filter((nz) => pumpsInRecon.includes(nz.pump_name))
        : form.pump_name
        ? nozzles.filter((nz) => nz.pump_name === form.pump_name)
        : nozzles;

    const set = new Set();
    const rateMap = {};

    baseNozzles.forEach((nz) => {
      if (nz?.fuel_type) {
        set.add(nz.fuel_type);
        if (
          rateMap[nz.fuel_type] == null &&
          nz?.rate != null &&
          nz.rate !== ""
        ) {
          rateMap[nz.fuel_type] = parseFloat(nz.rate) || 0;
        }
      }
    });

    return {
      availableFuelTypes: Array.from(set),
      fuelRateMap: rateMap,
    };
  }, [nozzles, form.pump_name, JSON.stringify(pumpMap)]);

  const getRateForFuelType = (fuelType) => {
    const r = fuelRateMap?.[fuelType];
    return r ? parseFloat(r) : 0;
  };

  const updateCustomerCreditRow = (idx, patch) => {
    setCustomerCredits((prev) => {
      const next = [...prev];
      const row = { ...next[idx], ...patch };

      const amt = parseFloat(row.amount) || 0;
      const rate = row.fuel_type ? getRateForFuelType(row.fuel_type) : 0;

      if (row.fuel_type && amt > 0 && rate > 0) {
        row.volume = (amt / rate).toFixed(3);
      } else {
        row.volume = "";
      }

      next[idx] = row;
      return next;
    });

    if (patch?.customer_id !== undefined) {
      const customerId = String(patch.customer_id || "");
      if (customerId) {
        loadCustomerCreditLimit(customerId).catch((err) =>
          console.error("Customer credit limit load error:", err)
        );
      }
    }
  };

  useEffect(() => {
    if (!showCustomerCredit) return;

    customerCredits.forEach((row) => {
      if (row.customer_id) {
        loadCustomerCreditLimit(row.customer_id).catch((err) =>
          console.error("Customer credit limit preload error:", err)
        );
      }
    });
  }, [showCustomerCredit]);

  const fetchLubeTotalForRecon = async (
    employeeId,
    openingDateTime,
    closingDateTime
  ) => {
    if (!employeeId || !openingDateTime || !closingDateTime) {
      setLubeReport({
        total_amount: 0,
        total_quantity: 0,
        sales: [],
        summaryByProduct: [],
        startDateTime: "",
        endDateTime: "",
      });
      return;
    }

    try {
      const res = await api.get("/api/lube-sales/report", {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          operatorId: employeeId,
          startDateTime: toSqlDateTime(openingDateTime),
          endDateTime: toSqlDateTime(closingDateTime),
          includeReconciled: false,
        },
      });

      setLubeReport({
        total_amount: parseFloat(res.data?.totals?.total_amount || 0),
        total_quantity: parseFloat(res.data?.totals?.total_quantity || 0),
        sales: res.data?.sales || [],
        summaryByProduct: res.data?.summaryByProduct || [],
        startDateTime: res.data?.startDateTime || "",
        endDateTime: res.data?.endDateTime || "",
      });
    } catch (err) {
      console.error("Error fetching lube report:", err);
      setLubeReport({
        total_amount: 0,
        total_quantity: 0,
        sales: [],
        summaryByProduct: [],
        startDateTime: "",
        endDateTime: "",
      });
    }
  };

  useEffect(() => {
    if (
      showReconcile &&
      recon.employee_id &&
      form.opening_reading_datetime &&
      form.closing_reading_datetime
    ) {
      fetchLubeTotalForRecon(
        recon.employee_id,
        form.opening_reading_datetime,
        form.closing_reading_datetime
      );
    }
  }, [
    showReconcile,
    recon.employee_id,
    form.opening_reading_datetime,
    form.closing_reading_datetime,
  ]);

  const updateMissingLubeRow = (idx, patch) => {
    setMissingLubeRows((prev) => {
      const next = [...prev];
      const current = { ...next[idx], ...patch };

      if (patch.product_id !== undefined) {
        const product = lubeProducts.find(
          (p) => String(p.id) === String(patch.product_id)
        );
        current.product_name = product?.product_name || "";
        current.unit_price = parseFloat(product?.unit_price || 0);
      }

      const qty = parseFloat(current.quantity || 0);
      const price = parseFloat(current.unit_price || 0);
      current.total_amount =
        qty > 0 && price > 0 ? parseFloat((qty * price).toFixed(2)) : 0;

      next[idx] = current;
      return next;
    });
  };

  const addMissingLubeRow = () => {
    setMissingLubeRows((prev) => [
      ...prev,
      {
        product_id: "",
        quantity: "",
        unit_price: 0,
        total_amount: 0,
        product_name: "",
        comment: "",
      },
    ]);
  };

  const removeMissingLubeRow = (idx) => {
    setMissingLubeRows((prev) => {
      if (prev.length === 1) {
        return [
          {
            product_id: "",
            quantity: "",
            unit_price: 0,
            total_amount: 0,
            product_name: "",
            comment: "",
          },
        ];
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const validMissingLubeRows = missingLubeRows.filter(
    (row) =>
      row.product_id &&
      parseFloat(row.quantity || 0) > 0 &&
      parseFloat(row.total_amount || 0) > 0
  );

  const manualLubeAdjustment = validMissingLubeRows.reduce(
    (sum, row) => sum + parseFloat(row.total_amount || 0),
    0
  );

  const lubeTotal = parseFloat(lubeReport.total_amount || 0);
  const finalLubeTotal = lubeTotal + manualLubeAdjustment;
  const grandTotal = fuelTotal + finalLubeTotal;

  const fuelBreakdownRows = useMemo(() => {
    const bucket = new Map();

    displayedReadings.forEach((row) => {
      const fuelType = getFuelName(row.nozzle_id) || "Unassigned";
      const volume = parseFloat(row.actual_dispensed_volume || 0);
      const amount = parseFloat(row.actual_amount || 0);
      const testingVolume = parseFloat(row.testing_liters || 0);
      const testingAmount = parseFloat(row.testing_amount || 0);

      if (!bucket.has(fuelType)) {
        bucket.set(fuelType, {
          fuelType,
          count: 0,
          volume: 0,
          amount: 0,
          testingVolume: 0,
          testingAmount: 0,
        });
      }

      const entry = bucket.get(fuelType);
      entry.count += 1;
      entry.volume += volume;
      entry.amount += amount;
      entry.testingVolume += testingVolume;
      entry.testingAmount += testingAmount;
    });

    return Array.from(bucket.values()).sort((a, b) =>
      String(a.fuelType).localeCompare(String(b.fuelType))
    );
  }, [displayedReadings, nozzles]);


  const pumpNozzles = form.pump_name
    ? nozzles.filter((nz) => nz.pump_name === form.pump_name)
    : [];

  const summaryCards = [
    {
      icon: FaGasPump,
      label: "Selected Nozzles",
      value: form.pump_name ? pumpNozzles.length : nozzles.length,
      helper: form.pump_name ? form.pump_name : "Across all pumps",
      tone: "blue",
    },
    {
      icon: FaFileInvoiceDollar,
      label: "Fuel Total",
      value: formatMoney(fuelTotal),
      helper: "Sale amount after testing adjustment",
      tone: "green",
    },
    {
      icon: FaVial,
      label: "Testing",
      value: `${testingLitersTotal.toFixed(3)} L`,
      helper: `${formatMoney(testingTotal)} testing amount`,
      tone: "amber",
    },
    {
      icon: FaHandshake,
      label: "Readings",
      value: displayedReadings.length,
      helper: "Rows ready for review",
      tone: "slate",
    },
  ];

  const createMissingLubeSales = async () => {
    if (validMissingLubeRows.length === 0) return;

    if (!recon.employee_id) {
      throw new Error("Employee is required before creating missing lube sales.");
    }

    if (!recon.shift) {
      throw new Error("Shift is required before creating missing lube sales.");
    }

    const saleDate = toSqlDateTime(form.closing_reading_datetime);

    for (const row of validMissingLubeRows) {
      const payload = {
        operator_id: recon.employee_id,
        shift: recon.shift,
        product_id: row.product_id,
        quantity: parseFloat(row.quantity || 0),
        total_amount: parseFloat(row.total_amount || 0),
        comment: row.comment || "Added during reconciliation",
        sale_date: saleDate,
      };

      await api.post("/api/lube-sales", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
    }
  };

  return (
    <div className="nread-page text-light">
      <NotificationToast notice={notice} onClose={() => setNotice(null)} />

      <section className="nread-hero">
        <div>
          <div className="nread-eyebrow">Operations Command</div>
          <h2>Nozzle readings</h2>
          <p>
            Capture opening and closing readings, review fuel sales, export data, and move
            directly into reconciliation from one mobile-friendly workspace.
          </p>
        </div>

        <div className="nread-hero-actions">
          <button className="btn nread-secondary-action" onClick={exportToExcel}>
            <FaDownload aria-hidden="true" />
            Excel
          </button>
          <button
            className="btn nread-primary-action"
            onClick={() => {
              if (!form.opening_reading_datetime || !form.closing_reading_datetime) {
                showNotice(
                  "warning",
                  "Time required",
                  "Can't continue without opening and closing time. Please check again for the opening and closing time."
                );
                return;
              }

              setShowReconcile(true);
              setLubeReport({
                total_amount: 0,
                total_quantity: 0,
                sales: [],
                summaryByProduct: [],
                startDateTime: "",
                endDateTime: "",
              });
              setRecon({
                date: new Date().toISOString().slice(0, 10),
                employee_id: "",
                shift: "",
                cash: 0,
                upi: 0,
                card: 0,
                hp_pay: 0,
                credit: 0,
                test: parseFloat(testingTotal.toFixed(2)),
                others: 0,
                fleet: 0,
                misc: 0,
                comment: "",
              });
              resetCustomerCredits();
              resetMissingLubeRows();
            }}
          >
            <FaHandshake aria-hidden="true" />
            Reconciliation
          </button>
        </div>
      </section>

      <section className="nread-summary-grid">
        {summaryCards.map(({ helper, icon: Icon, label, tone, value }) => (
          <article className={`nread-summary-card ${tone}`} key={label}>
            <span className="nread-summary-icon">
              <Icon aria-hidden="true" />
            </span>
            <div>
              <span>{label}</span>
              <strong>{value}</strong>
              <em>{helper}</em>
            </div>
          </article>
        ))}
      </section>

      <div className="card-like nread-form">
        <div className="nread-card-head">
          <div>
            <span>Reading Entry</span>
            <h3>{editId ? "Update nozzle reading" : "Capture shift readings"}</h3>
          </div>
        </div>

        <div className="row g-2 mb-2">
         
        </div>

        <label className="form-label">Pump</label>
        <select
  className="form-select my-2 nread-pump-select"
  value={form.pump_name}
  onChange={async (e) => {
    const pump_name = e.target.value;

    try {
      // Always fetch fresh nozzle data on pump change
      const freshNozzles = await fetchNozzles();

      const related = pump_name
        ? freshNozzles.filter((nz) => nz.pump_name === pump_name)
        : [];

      let openingDt = "";

      if (related.length > 0) {
        const dtList = related
          .map((nz) => String(nz.latest_reading_datetime || "").trim())
          .filter(Boolean);

        if (dtList.length > 0) {
          // No Date parsing, no timezone conversion
          // SQL datetime string sorting works fine lexicographically
          const latestDtRaw = dtList.sort((a, b) => b.localeCompare(a))[0];
          openingDt = formatDatetime(latestDtRaw);
        }
      }

      const init = {};
      related.forEach((nz) => {
        init[nz.id] = {
          opening_reading: nz.latest_reading ?? "",
          closing_reading: "",
        };
      });

      setForm((prev) => ({
        ...prev,
        pump_name,
        nozzle_id: "",
        opening_reading_datetime: openingDt,
        closing_reading_datetime: pump_name ? prev.closing_reading_datetime : "",
      }));

      setPumpNozzleReadings(pump_name ? init : {});
    } catch (err) {
      console.error("Error refreshing nozzles on pump change:", err);
      showNotice(
        "error",
        "Pump details failed",
        "Failed to fetch latest pump reading details."
      );
    }
  }}
>
  <option value="">Select Pump</option>
  {[...new Set(nozzles.map((nz) => nz.pump_name))].map((pump) => (
    <option key={pump} value={pump}>
      {pump}
    </option>
  ))}
</select>

        {!form.pump_name && (
          <div className="nread-disabled-entry mt-3">
            <button
              type="button"
              className="nread-disabled-entry-guard"
              onClick={showPumpFirstWarning}
              aria-label="Select a pump before entering readings"
            />
            <div className="nread-disabled-entry-copy">
              <FaGasPump aria-hidden="true" />
              <div>
                <strong>Select a pump to unlock readings</strong>
                <span>Opening and closing reading fields stay locked until a pump is chosen.</span>
              </div>
            </div>
            <div className="nread-reading-inputs mt-3">
              <input type="number" className="form-control" placeholder="Opening" disabled />
              <input type="number" className="form-control" placeholder="Closing" disabled />
            </div>
          </div>
        )}

        {form.pump_name && (
          <div className="mt-2">
            <div className="row g-3">
              {pumpNozzles.map((nz) => {
                const nozzleState = pumpNozzleReadings[nz.id] || {
                  opening_reading: "",
                  closing_reading: "",
                };

                const fuelType = nz.fuel_type;
                const fuelColor = getFuelColor(fuelType);

                return (
                  <div key={nz.id} className="col-md-4 col-sm-6">
                    <div className="nread-nozzle-card">
                      <div className="form-label fw-bold mb-1 text-info d-flex align-items-center justify-content-between">
                        <span>
                          {nz.name}
                          {fuelType ? ` - ${fuelType}` : ""}
                        </span>
                        <span className="nread-fuel-swatch" style={{ backgroundColor: fuelColor }} />
                      </div>

                      <div className="nread-reading-inputs">
                        <div className="nread-input-guard">
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Opening"
                            value={nozzleState.opening_reading}
                            disabled={isManager}
                            onChange={(e) =>
                              setPumpNozzleReadings((prev) => ({
                                ...prev,
                                [nz.id]: {
                                  ...prev[nz.id],
                                  opening_reading: e.target.value,
                                },
                              }))
                            }
                          />
                          {isManager && (
                            <button
                              type="button"
                              className="nread-field-guard"
                              onClick={showOpeningLockedWarning}
                              aria-label="Opening reading is locked for managers"
                            />
                          )}
                        </div>

                        <input
                          type="number"
                          className="form-control"
                          placeholder="Closing"
                          value={nozzleState.closing_reading}
                          onChange={(e) =>
                            setPumpNozzleReadings((prev) => ({
                              ...prev,
                              [nz.id]: {
                                ...prev[nz.id],
                                closing_reading: e.target.value,
                              },
                            }))
                          }
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        <div className="row g-2 mb-2">
          <div className="col-md-6">
            <label className="form-label">Opening Time</label>
            <div className="nread-input-guard">
              <input
                type="datetime-local"
                className="form-control"
                disabled={!form.pump_name || isManager}
                value={form.opening_reading_datetime}
                onChange={(e) =>
                  setForm({ ...form, opening_reading_datetime: e.target.value })
                }
              />
              {!form.pump_name && (
                <button
                  type="button"
                  className="nread-field-guard"
                  onClick={showPumpFirstWarning}
                  aria-label="Select a pump before setting opening time"
                />
              )}
              {form.pump_name && isManager && (
                <button
                  type="button"
                  className="nread-field-guard"
                  onClick={showOpeningLockedWarning}
                  aria-label="Opening time is locked for managers"
                />
              )}
            </div>
          </div>

          <div className="col-md-6">
            <label className="form-label">Closing Time</label>
            <div className="nread-input-guard">
              <input
                type="datetime-local"
                className="form-control"
                disabled={!form.pump_name}
                value={form.closing_reading_datetime}
                onChange={(e) =>
                  setForm({ ...form, closing_reading_datetime: e.target.value })
                }
              />
              {!form.pump_name && (
                <button
                  type="button"
                  className="nread-field-guard"
                  onClick={showPumpFirstWarning}
                  aria-label="Select a pump before setting closing time"
                />
              )}
            </div>
          </div>
          </div>
        <div className="d-flex gap-2 mt-2">
          <button className="btn btn-success" onClick={save}>
            <FaSave aria-hidden="true" />
            {editId ? "Update" : "Save"} Reading
          </button>

          {editId && (
            <button className="btn btn-secondary" onClick={reset}>
              Cancel
            </button>
          )}
        </div>
      </div>

      <div className="row mb-3 align-items-end card-like nread-filters">
        <div className="col-lg-6">
          <label className="form-label d-block">Testing</label>
          <div className="nread-segmented">
            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="testingNo"
                checked={testingMode === "no"}
                onChange={() => setTestingMode("no")}
              />
              <label className="form-check-label" htmlFor="testingNo">
                No
              </label>
            </div>

            <div className="form-check">
              <input
                className="form-check-input"
                type="checkbox"
                id="testingYes"
                checked={testingMode === "yes"}
                onChange={() => setTestingMode("yes")}
              />
              <label className="form-check-label" htmlFor="testingYes">
                Yes
              </label>
            </div>
          </div>
        </div>

        <div className="col-lg-6">
          <div className="nread-total-strip">
            <div>
              <span>Fuel Total</span>
              <strong>₹{fuelTotal.toFixed(2)}</strong>
            </div>
            <div>
              <span>Testing Liters</span>
              <strong>{testingLitersTotal.toFixed(3)} L</strong>
            </div>
            <div>
              <span>Testing Amount</span>
              <strong>₹{testingTotal.toFixed(2)}</strong>
            </div>
          </div>
        </div>
      </div>

      <div className="nread-reconcile-strip card-like">
        <div className="nread-reconcile-copy">
          <span>Reconciliation</span>
          <strong>Ready when you are</strong>
          <p>
            Open the reconciliation panel from here after reviewing the readings and totals.
          </p>
        </div>
        <button
          type="button"
          className="btn btn-success nread-reconcile-btn"
          onClick={() => setShowReconcile(true)}
        >
          Open Reconciliation
        </button>
      </div>

      <div className="table-responsive card-like nread-table">
        <table className="table table-bordered">
          <thead>
            <tr>
              <th>Id</th>
              <th>Pump</th>
              <th>Fuel Type</th>
              <th>Nozzle</th>
              <th>Opening</th>
              <th>Closing</th>
              <th>Dispensed</th>
              <th>Testing</th>
              <th>Amount</th>
              <th>Open Time</th>
              <th>Close Time</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {displayedReadings.map((r) => {
              const nozzleObj = nozzles.find(
                (nz) => String(nz.id) === String(r.nozzle_id)
              );
              const fuelType = nozzleObj?.fuel_type;
              const fuelColor = getFuelColor(fuelType);

              return (
                <tr key={r.id}>
                  <td data-label="Id">{r.id}</td>
                  <td data-label="Pump">{getPumpName(r.nozzle_id)}</td>
                  <td data-label="Fuel Type">{fuelType || getFuelName(r.nozzle_id)}</td>
                  <td data-label="Nozzle">
                    {nozzleObj
                      ? `${nozzleObj.name}${fuelType ? ` - ${fuelType}` : ""}`
                      : r.nozzle_id}
                    {fuelType && (
                      <span className="nread-fuel-swatch mini" style={{ backgroundColor: fuelColor }} />
                    )}
                  </td>
                  <td data-label="Opening">{r.opening_reading}</td>
                  <td data-label="Closing">{r.closing_reading}</td>
                  <td data-label="Dispensed">{parseFloat(r.actual_dispensed_volume || 0).toFixed(3)}</td>
                  <td data-label="Testing">
                    {testingMode === "yes"
                      ? `${parseFloat(r.testing_liters || 0).toFixed(3)} L / ₹${parseFloat(
                          r.testing_amount || 0
                        ).toFixed(2)}`
                      : "-"}
                  </td>
                  <td data-label="Amount">₹{parseFloat(r.actual_amount || 0).toFixed(2)}</td>
                  <td data-label="Open Time">{displayDateTime(r.opening_reading_datetime)}</td>
                  <td data-label="Close Time">{displayDateTime(r.closing_reading_datetime)}</td>
                  <td data-label="Actions" className="nread-row-actions">
                    <button
                      className="btn btn-sm btn-warning me-2 shiny-warn"
                      onClick={() => startEdit(r)}
                    >
                      <FaEdit aria-hidden="true" />
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger shiny-danger"
                      onClick={() => del(r.id)}
                    >
                      <FaTrash aria-hidden="true" />
                      Delete
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showReconcile && (
        <div className="reconcile-overlay">
          <div className="reconcile-modal">
            <div className="reconcile-header">
              <h5>Reconciliation</h5>
              <button className="btn-close" onClick={() => setShowReconcile(false)}>
                ×
              </button>
            </div>

            <div className="reconcile-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <label className="form-label">Date</label>
                  <input
                    type="date"
                    className="form-control"
                    value={recon.date}
                    onChange={(e) => setRecon({ ...recon, date: e.target.value })}
                  />
                </div>

                <div className="col-md-4">
                  <label className="form-label">Employee</label>
                  <select
                    className="form-select"
                    value={recon.employee_id}
                    onChange={(e) => setRecon({ ...recon, employee_id: e.target.value })}
                  >
                    <option value="">Select Employee</option>
                    {employees
                      .filter((emp) => emp.role === "operator" || emp.role === "manager")
                      .map((emp) => (
                        <option key={emp.id} value={emp.id}>
                          {emp.name}
                        </option>
                      ))}
                  </select>
                </div>

                <div className="col-md-4">
                  <label className="form-label">Shift</label>
                  <select
                    className="form-select"
                    value={recon.shift}
                    onChange={(e) => setRecon({ ...recon, shift: e.target.value })}
                  >
                    <option value="">Select Shift</option>
                    <option value="morning">Morning</option>
                    <option value="night">Night</option>
                    <option value="24Hours">24 Hours</option>
                  </select>
                </div>
              </div>

              <hr />

              <div className="row g-2 mb-3">
                <div className="col-md-3">
                  <div className="p-3 border rounded-3 bg-dark bg-opacity-25 h-100">
                    <div className="small text-muted">Fuel Total (Sale Only)</div>
                    <div className="fw-bold fs-5">₹{fuelTotal.toFixed(2)}</div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="p-3 border rounded-3 bg-dark bg-opacity-25 h-100">
                    <div className="small text-muted">Testing (Auto)</div>
                    <div className="fw-bold fs-5">₹{testingTotal.toFixed(2)}</div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="p-3 border rounded-3 bg-dark bg-opacity-25 h-100">
                    <div className="small text-muted">Lube Total (Auto)</div>
                    <div className="fw-bold fs-5">₹{lubeTotal.toFixed(2)}</div>
                  </div>
                </div>

                <div className="col-md-3">
                  <div className="p-3 border rounded-3 bg-dark bg-opacity-25 h-100">
                    <div className="small text-muted">Missing Lube Added</div>
                    <div className="fw-bold fs-5">
                      ₹{manualLubeAdjustment.toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="col-md-12 mt-2">
                  <div
                    className="p-3 rounded-3 h-100"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(34,197,94,0.18), rgba(16,185,129,0.10))",
                      border: "1px solid rgba(74,222,128,0.35)",
                      boxShadow: "0 10px 25px rgba(0,0,0,0.18)",
                    }}
                  >
                    <div className="small text-light">Grand Total</div>
                    <div className="fw-bold fs-5 text-white">
                      ₹{grandTotal.toFixed(2)}
                    </div>
                  </div>
                </div>

                <div className="col-md-4 mt-2">
                  <label className="form-label">Reconciliation Discount</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="form-control"
                    value={recon.discount_amount}
                    onChange={(e) =>
                      setRecon({
                        ...recon,
                        discount_amount: parseFloat(e.target.value || 0),
                      })
                    }
                    placeholder="Enter discount now"
                  />
                </div>
              </div>

              <div className="row g-2 mb-3">
                <div className="col-md-6">
                  <label className="form-label">Lube Fetch Window</label>
                  <div
                    className="rounded-3 px-3 py-3"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.92), rgba(226,232,240,0.88))",
                      color: "#0f172a",
                      border: "1px solid rgba(148,163,184,0.45)",
                      boxShadow: "0 10px 24px rgba(15,23,42,0.12)",
                      minHeight: "58px",
                      display: "flex",
                      alignItems: "center",
                      fontWeight: 600,
                    }}
                  >
                    {form.opening_reading_datetime && form.closing_reading_datetime
                      ? `${displayDateTime(form.opening_reading_datetime)}  to  ${displayDateTime(
                          form.closing_reading_datetime
                        )}`
                      : "Opening / Closing time not available"}
                  </div>
                </div>

                <div className="col-md-6">
                  <label className="form-label">Auto Fetched Lube Count</label>
                  <div
                    className="rounded-3 px-3 py-3"
                    style={{
                      background:
                        "linear-gradient(135deg, rgba(255,255,255,0.92), rgba(226,232,240,0.88))",
                      color: "#0f172a",
                      border: "1px solid rgba(148,163,184,0.45)",
                      boxShadow: "0 10px 24px rgba(15,23,42,0.12)",
                      minHeight: "58px",
                      display: "flex",
                      alignItems: "center",
                      fontWeight: 600,
                    }}
                  >
                    {lubeReport.sales.length} sale(s) found in this shift window
                  </div>
                </div>
              </div>

              <div className="mb-3">
                <label className="form-label">Add Missing Lube Sales</label>
                <div
                  className="rounded-3 p-3"
                  style={{
                    background: "rgba(255,255,255,0.06)",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                >
                  {missingLubeRows.map((row, idx) => {
                    const selectedProduct = lubeProducts.find(
                      (p) => String(p.id) === String(row.product_id)
                    );

                    return (
                      <div key={idx} className="row g-2 align-items-end mb-2">
                        <div className="col-md-4">
                          <label className="form-label small">Lube Product</label>
                          <select
                            className="form-select"
                            value={row.product_id}
                            onChange={(e) =>
                              updateMissingLubeRow(idx, { product_id: e.target.value })
                            }
                          >
                            <option value="">Select Lube</option>
                            {lubeProducts.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.product_name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div className="col-md-2">
                          <label className="form-label small">Quantity</label>
                          <input
                            type="number"
                            step="0.01"
                            className="form-control"
                            value={row.quantity}
                            onChange={(e) =>
                              updateMissingLubeRow(idx, { quantity: e.target.value })
                            }
                            placeholder="Qty"
                          />
                        </div>

                        <div className="col-md-2">
                          <label className="form-label small">Rate</label>
                          <input
                            type="number"
                            className="form-control"
                            value={row.unit_price}
                            disabled
                          />
                        </div>

                        <div className="col-md-2">
                          <label className="form-label small">Amount</label>
                          <input
                            type="number"
                            className="form-control"
                            value={row.total_amount}
                            disabled
                          />
                        </div>

                        <div className="col-md-1">
                          <label className="form-label small">Stock</label>
                          <div className="form-control text-center">
                            {selectedProduct?.quantity_in_stock ?? "-"}
                          </div>
                        </div>

                        <div className="col-md-1">
                          <button
                            className="btn btn-outline-danger w-100"
                            onClick={() => removeMissingLubeRow(idx)}
                          >
                            ×
                          </button>
                        </div>

                        <div className="col-md-12">
                          <input
                            type="text"
                            className="form-control"
                            value={row.comment}
                            onChange={(e) =>
                              updateMissingLubeRow(idx, { comment: e.target.value })
                            }
                            placeholder="Optional note for missed lube sale"
                          />
                        </div>
                      </div>
                    );
                  })}

                  <div className="d-flex justify-content-between align-items-center mt-3">
                    <button
                      className="btn btn-outline-info btn-sm"
                      onClick={addMissingLubeRow}
                    >
                      + Add Missing Lube
                    </button>

                    <div className="fw-bold">
                      Missing Lube Total: ₹{manualLubeAdjustment.toFixed(2)}
                    </div>
                  </div>
                </div>
              </div>

              {lubeReport.sales.length > 0 && (
                <div className="mb-3">
                  <label className="form-label">Auto-detected Lube Sales</label>
                  <div className="table-responsive">
                    <table className="table table-sm table-bordered">
                      <thead>
                        <tr>
                          <th>Date</th>
                          <th>Product</th>
                          <th>Qty</th>
                          <th>Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lubeReport.sales.map((sale) => (
                          <tr key={sale.sale_id}>
                            <td>{displayDateTime(sale.sale_date)}</td>
                            <td>{sale.product_name}</td>
                            <td>{sale.quantity}</td>
                            <td>₹{parseFloat(sale.total_amount || 0).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              <div className="mb-3">
                <label className="form-label">Fuel-wise Split</label>
                <div className="table-responsive">
                  <table className="table table-sm table-bordered align-middle">
                    <thead>
                      <tr>
                        <th>Fuel Type</th>
                        <th className="text-end">Count</th>
                        <th className="text-end">Volume (L)</th>
                        <th className="text-end">Testing (L)</th>
                        <th className="text-end">Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {fuelBreakdownRows.length ? (
                        fuelBreakdownRows.map((row) => (
                          <tr key={row.fuelType}>
                            <td>{row.fuelType}</td>
                            <td className="text-end">{row.count}</td>
                            <td className="text-end">{row.volume.toFixed(3)}</td>
                            <td className="text-end">{row.testingVolume.toFixed(3)}</td>
                            <td className="text-end">₹{row.amount.toFixed(2)}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={5} className="text-center text-muted">
                            No fuel rows available for this reconciliation window
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="row g-1">
                {["cash", "upi", "card", "hp_pay", "others", "fleet", "misc"].map(
                  (field) => (
                    <div key={field} className="col-md-3">
                      <label className="form-label text-capitalize">
                        {field.replace("hp_pay", "Company Rewards")}
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        className="form-control"
                        value={recon[field]}
                        onChange={(e) =>
                          setRecon({
                            ...recon,
                            [field]: parseFloat(e.target.value || 0),
                          })
                        }
                      />
                    </div>
                  )
                )}
              </div>

              <div className="row g-1 mt-2">
                <div className="col-md-3">
                  <label className="form-label">Testing (Auto)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={recon.test}
                    readOnly
                  />
                </div>
              </div>

              <div className="row g-1 mt-2">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <label className="form-label mb-0">Credit</label>
                  <span className="badge text-bg-info">
                    {customerCredits.filter((row) => row.customer_id || row.amount || row.fuel_type).length} rows
                  </span>
                </div>
                <div className="d-flex gap-2">
                  <input
                    type="number"
                    step="0.01"
                    className="form-control"
                    value={recon.credit}
                    onChange={(e) =>
                      setRecon({
                        ...recon,
                        credit: parseFloat(e.target.value || 0),
                      })
                    }
                  />
                  <button
                    className="btn btn-outline-info"
                    onClick={() => setShowCustomerCredit(true)}
                  >
                    Manage
                  </button>
                </div>
                <div className="small text-muted mt-1">
                  See how credit is split across customers before you save reconciliation.
                </div>
              </div>

              <div className="mt-3">
                <label className="form-label">Comment / Note</label>
                <textarea
                  rows="3"
                  className="form-control"
                  placeholder="Add remarks about reconciliation..."
                  value={recon.comment}
                  onChange={(e) => setRecon({ ...recon, comment: e.target.value })}
                />
              </div>
            </div>

            <div className="reconcile-footer mt-3 d-flex justify-content-between align-items-center">
              <div className="fw-bold">
                Remaining: ₹{computeRemaining(grandTotal, recon).toFixed(2)}
              </div>

              <button
  className="btn btn-success"
  disabled={savingRecon}
  onClick={() => setShowConfirmSave(true)}
>
  {savingRecon ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {showConfirmSave && (
        <div className="reconcile-overlay">
    <div className="reconcile-modal" style={{ maxWidth: 400 }}>
      <div className="reconcile-header">
        <h5>Confirm Save</h5>
        <button
          className="btn-close"
          onClick={() => setShowConfirmSave(false)}
        >
          ×
        </button>
      </div>

      <div className="reconcile-body text-center">
        <p className="mb-3">
          Are you sure you want to save the reconciliation data?
        </p>

        <div className="d-flex justify-content-center gap-3">
          <button
            className="btn btn-secondary"
            onClick={() => setShowConfirmSave(false)}
          >
            No
          </button>

          <button
            className="btn btn-success"
            disabled={savingRecon}
            onClick={async () => {
              setShowConfirmSave(false);

              const remainingAmt = computeRemaining(grandTotal, recon);
              const reconciledReadingIds = displayedReadings.map((r) => r.id);

              const testingBreakup = displayedReadings.map((r) => ({
                nozzle_reading_id: r.id,
                testing_liters: parseFloat((r.testing_liters || 0).toFixed(3)),
                testing_amount: parseFloat((r.testing_amount || 0).toFixed(2)),
                actual_dispensed_volume: parseFloat(
                  (r.actual_dispensed_volume || 0).toFixed(3)
                ),
                actual_amount: parseFloat((r.actual_amount || 0).toFixed(2)),
              }));

              if (!recon.employee_id) {
                showNotice("error", "Employee required", "Please select employee.");
                return;
              }

              if (!recon.shift) {
                showNotice("error", "Shift required", "Please select shift.");
                return;
              }

              if (!form.opening_reading_datetime || !form.closing_reading_datetime) {
                showNotice(
                  "warning",
                  "Time required",
                  "Can't save the reconciliation without opening and closing time. Please check again for the opening and closing time."
                );
                return;
              }

              const discountAmount = n(recon.discount_amount);
              if (discountAmount < 0) {
                showNotice("warning", "Invalid discount", "Discount cannot be negative.");
                return;
              }
              if (discountAmount > grandTotal) {
                showNotice(
                  "warning",
                  "Discount too high",
                  "Discount cannot be greater than the grand total."
                );
                return;
              }

              try {
                setSavingRecon(true);

                await createMissingLubeSales();

                const payload = {
                  employee_id: recon.employee_id,
                  shift: recon.shift,
                  recon_date: recon.date,
                  pump_nozzle_summary: pumpMap,
                  nozzle_reading_ids: reconciledReadingIds,

                  fuel_total: fuelTotal,
                  lube_total: lubeTotal,
                  lube_adjustment: manualLubeAdjustment,
                  total_amount: grandTotal,

                  cash_amount: recon.cash,
                  upi_amount: recon.upi,
                  card_amount: recon.card,
                  hp_pay_amount: recon.hp_pay,
                  credit_amount: recon.credit,
                  test_amount: recon.test,
                  others_amount: recon.others,
                  fleet: recon.fleet,
                  misc: recon.misc,
                  discount_amount: discountAmount,
                  comment: recon.comment,
                  customer_credits: customerCredits,

                  lube_start_datetime: toSqlDateTime(form.opening_reading_datetime),
                  lube_end_datetime: toSqlDateTime(form.closing_reading_datetime),
                  testing_mode: testingMode,
                  testing_liters_total: testingLitersTotal,
                  testing_breakup: testingBreakup,
                };

                await api.post("/api/reconciliation", payload, {
                  headers: { Authorization: `Bearer ${token}` },
                });

                if (remainingAmt > 0) {
                  showNotice(
                    "info",
                    "Credit difference recorded",
                    `Credit of ₹${remainingAmt.toFixed(
                      2
                    )} recorded for difference while reconcile.`
                  );
                } else if (remainingAmt === 0) {
                  showNotice("success", "Perfect collection", "Cash collected perfectly.");
                } else {
                  showNotice(
                    "error",
                    "Cash mismatch",
                    "Employee cash mismatch. Negative balance detected."
                  );
                }

                setShowReconcile(false);
                setLubeReport({
                  total_amount: 0,
                  total_quantity: 0,
                  sales: [],
                  summaryByProduct: [],
                  startDateTime: "",
                  endDateTime: "",
                });
                setRecon({
                  date: "",
                  employee_id: "",
                  shift: "",
                  cash: 0,
                  upi: 0,
                  card: 0,
                  hp_pay: 0,
                  credit: 0,
                  test: 0,
                  others: 0,
                  fleet: 0,
                  misc: 0,
                  discount_amount: 0,
                  comment: "",
                });
                resetCustomerCredits();
                resetMissingLubeRows();
                fetchReadings();
              } catch (err) {
                console.error("Error saving reconciliation:", err);
                showNotice(
                  "error",
                  "Reconciliation failed",
                  err?.response?.data?.error ||
                    "Error saving reconciliation data."
                );
              } finally {
                setSavingRecon(false);
              }
            }}
          >
            Yes
          </button>
        </div>
      </div>
    </div>
  </div>
)}



      {showCustomerCredit && (
        <div className="credit-overlay">
          <div className="credit-modal">
            <div className="credit-header d-flex justify-content-between align-items-center">
              <h6>Manage Customer Credits</h6>
              <button className="btn-close" onClick={() => setShowCustomerCredit(false)}>
                ×
              </button>
            </div>

            <div className="credit-body">
              {availableFuelTypes.length === 0 && (
                <div className="alert alert-warning py-2 mb-2">
                  No fuel types found. Please ensure nozzles have fuel_type and rate in
                  master.
                </div>
              )}

              {customerCredits.map((c, i) => (
                <div key={i} className="row g-2 align-items-center mb-2">
                  <div className="col-md-3">
                    <select
                      className="form-select"
                      value={c.customer_id}
                      onChange={(e) =>
                        updateCustomerCreditRow(i, { customer_id: e.target.value })
                      }
                    >
                      <option value="">Select Customer</option>
                      {customers.map((cust) => (
                        <option key={cust.id} value={cust.id}>
                          {cust.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-2">
                    <select
                      className="form-select"
                      value={c.fuel_type}
                      onChange={(e) =>
                        updateCustomerCreditRow(i, { fuel_type: e.target.value })
                      }
                    >
                      <option value="">Select Fuel</option>
                      {availableFuelTypes.map((ft) => (
                        <option key={ft} value={ft}>
                          {ft}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-2">
                    {c.customer_id ? (
                      <div className="small text-danger mb-1" style={{ fontSize: "0.72rem" }}>
                        *{(() => {
                          const block = customerCreditLimitMap[String(c.customer_id)] || {};
                          if (block?.has_limit && Number(block.max_credit_amount || 0) > 0) {
                            return ` Max allowed ₹${Number(block.max_credit_amount).toFixed(2)}`;
                          }
                          return " No credit block set";
                        })()}
                      </div>
                    ) : null}
                    <input
                      type="number"
                      step="0.01"
                      className="form-control"
                      placeholder="Amount"
                      value={c.amount}
                      onChange={(e) =>
                        updateCustomerCreditRow(i, { amount: e.target.value })
                      }
                    />
                    {c.customer_id ? (
                      <div className="small text-danger mt-1" style={{ fontSize: "0.72rem" }}>
                        *{(() => {
                          const block = customerCreditLimitMap[String(c.customer_id)] || {};
                          if (block?.has_limit && Number(block.max_credit_amount || 0) > 0) {
                            const appliedAmount = Number(c.amount || 0);
                            const remainingAfterAmount = Math.max(
                              Number(block.max_credit_amount || 0) -
                                Number(block.running_credit || 0) -
                                appliedAmount,
                              0
                            );
                            return ` Remaining after this amount ₹${remainingAfterAmount.toFixed(2)}`;
                          }
                          return " No credit block set";
                        })()}
                      </div>
                    ) : null}
                  </div>

                  <div className="col-md-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Volume (L)"
                      value={c.volume}
                      disabled
                    />
                  </div>

                  <div className="col-md-2">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Comment"
                      value={c.comment}
                      onChange={(e) =>
                        updateCustomerCreditRow(i, { comment: e.target.value })
                      }
                    />
                  </div>

                  <div className="col-md-1">
                    <button
                      className="btn btn-outline-danger btn-sm"
                      onClick={() =>
                        setCustomerCredits(
                          customerCredits.filter((_, idx) => idx !== i)
                        )
                      }
                    >
                      ×
                    </button>
                  </div>

                  {c.fuel_type && (
                    <div className="col-12">
                      <div className="small text-muted">
                        Rate used: ₹{getRateForFuelType(c.fuel_type) || 0} / L
                      </div>
                    </div>
                  )}
                </div>
              ))}

              <button
                className="btn btn-outline-primary btn-sm"
                onClick={() =>
                  setCustomerCredits([
                    ...customerCredits,
                    {
                      customer_id: "",
                      fuel_type: "",
                      amount: "",
                      volume: "",
                      comment: "",
                    },
                  ])
                }
              >
                + Add Row
              </button>
            </div>

            <div className="credit-footer mt-3 text-end">
              <button
                className="btn btn-success"
                onClick={async () => {
                  const totalsByCustomer = new Map();
                  for (const row of customerCredits) {
                    const customerId = Number(row.customer_id);
                    const amount = Number(row.amount || 0);
                    if (!customerId || amount <= 0) continue;
                    totalsByCustomer.set(
                      customerId,
                      Number((totalsByCustomer.get(customerId) || 0) + amount)
                    );
                  }

                  for (const [customerId, amount] of totalsByCustomer.entries()) {
                    let block = customerCreditLimitMap[String(customerId)];
                    if (!block) {
                      block = await loadCustomerCreditLimit(customerId);
                    }

                    if (
                      block?.has_limit &&
                      Number(block.max_credit_amount || 0) > 0 &&
                      Number(block.running_credit || 0) + amount >
                        Number(block.max_credit_amount || 0) + 0.00001
                    ) {
                      const currentRunning = Number(block.running_credit || 0);
                      const maxAllowed = Number(block.max_credit_amount || 0);
                      const customerName = getCustomerDisplayName(customerId);
                      showNotice(
                        "warning",
                        "Customer credit blocked",
                        `${customerName} is already running credit of ₹${currentRunning.toFixed(
                          2
                        )}. This credit adds ₹${amount.toFixed(
                          2
                        )}, which exceeds the allowed limit of ₹${maxAllowed.toFixed(2)}.`
                      );
                      return;
                    }
                  }

                  const totalCustomerCredit = customerCredits.reduce(
                    (sum, row) => sum + (parseFloat(row.amount) || 0),
                    0
                  );
                  setRecon((prev) => ({ ...prev, credit: totalCustomerCredit }));
                  setShowCustomerCredit(false);
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}



